import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import * as Icon from 'ubs-icons';
import UBadge from '../UBadge';
import UCount from '../UCount';
import UNewTag from '../UNewTag';
import UCustomScrollBar from '../UCustomScrollBar';
import UPopup from '../UPopup';
import { COLOR } from '../common/constant';
import { readCollapsed, writeCollapsed } from './storage';

const hasAccess = (item) => !!item?.isAccess;

const accessibleInner = (item) => (Array.isArray(item?.inner_menu) ? item.inner_menu : []).filter(hasAccess);

const USidebarV2 = (props) => {
    const {
        list, active, innerActive, onClick, onInnerClick,
        header, footer, beforeNav, open, onClose,
        collapsed: collapsedProp, onCollapsedChange, collapsible, storageKey, defaultCollapsed,
        className, width, collapsedWidth,
    } = props;

    const isControlled = typeof collapsedProp === 'boolean';
    const [collapsedState, setCollapsedState] = useState(() => readCollapsed(storageKey, defaultCollapsed));
    const collapsed = isControlled ? collapsedProp : collapsedState;

    const [expandedKey, setExpandedKey] = useState(null);
    // Collapsed sub menus are portaled to <body> and positioned from the row's rect, because the
    // rail lives inside UCustomScrollBar, whose overflow would otherwise clip them away.
    const [flyout, setFlyout] = useState(null);

    const items = useMemo(() => (Array.isArray(list) ? list : []).filter(hasAccess), [list]);

    const activeParentRoute = useMemo(() => {
        // The top level route decides first. Inner route names are not unique across the menu
        // (hrms' inbox tab `/inbox/attendance` collides with Attendance's own `attendance` child),
        // so matching on the inner route alone used to light up an unrelated parent.
        const current = items.find(item => item?.route === active);
        if (current) return accessibleInner(current).length ? current.route : null;

        // Inner route names repeat across the menu (`dashboard` sits under four different hrms
        // parents), so this only resolves when exactly one parent owns the route. Taking the first
        // match would be a guess, and a wrong guess is what highlights an unrelated parent.
        const owners = items.filter(item => accessibleInner(item).some(inner => inner?.route === innerActive));
        return owners.length === 1 ? owners[0].route : null;
    }, [items, active, innerActive]);

    // Assigned unconditionally: guarding on a truthy value left the previous parent expanded after
    // navigating to a top level page that has no children of its own.
    useEffect(() => {
        setExpandedKey(activeParentRoute);
    }, [activeParentRoute]);

    useEffect(() => {
        if (collapsed) setExpandedKey(activeParentRoute);
        setFlyout(null);
    }, [collapsed, activeParentRoute]);

    useEffect(() => {
        if (!flyout) return undefined;

        const closeOnOutside = (event) => {
            if (event.target?.closest?.('.u_sbv2-flyout, .u_sbv2-row')) return;
            setFlyout(null);
        };

        const closeOnEsc = (event) => {
            if (event.key === 'Escape') setFlyout(null);
        };

        const closeOnScroll = (event) => {
            if (event.target?.closest?.('.u_sbv2-flyout')) return;
            setFlyout(null);
        };

        document.addEventListener('mousedown', closeOnOutside);
        document.addEventListener('keydown', closeOnEsc);
        document.addEventListener('scroll', closeOnScroll, true);

        return () => {
            document.removeEventListener('mousedown', closeOnOutside);
            document.removeEventListener('keydown', closeOnEsc);
            document.removeEventListener('scroll', closeOnScroll, true);
        };
    }, [flyout]);

    const openFlyout = (item, rowEl) => {
        const inner = accessibleInner(item);
        if (!rowEl || !inner.length) return;

        const rect = rowEl.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const menuHeight = inner.length * 35;
        const offsetTop = rect.top;

        let top = offsetTop;
        let arrowTop = 10;

        if ((menuHeight + offsetTop) > windowHeight) {
            top = offsetTop - (((menuHeight + offsetTop) - windowHeight) + 30);
            if (top < 0) top = 35;
            arrowTop = (offsetTop - top) + 10;
        }

        setFlyout({ route: item?.route, items: inner, top, arrowTop, left: rect.right });
    };

    const setCollapsed = (next) => {
        if (!isControlled) setCollapsedState(next);
        writeCollapsed(storageKey, next);
        onCollapsedChange?.(next);
    };

    const handleItemClick = (item, index, event) => {
        const inner = accessibleInner(item);

        if (!inner.length) {
            setFlyout(null);
            onClick?.(item, index, event);
            return;
        }

        if (collapsed) {
            if (flyout?.route === item?.route) setFlyout(null);
            else openFlyout(item, event?.currentTarget);
            return;
        }

        const isOpen = expandedKey === item.route;
        setExpandedKey(isOpen ? null : item.route);
        if (!isOpen && item.route !== activeParentRoute) onClick?.(item, index, event);
    };

    const handleInnerClick = (event, inner, parentRoute) => {
        event.stopPropagation();
        setFlyout(null);
        onInnerClick?.(event, inner, parentRoute);
    };

    const isInnerActive = (inner) => inner?.route === innerActive
        || String(inner?.route || '').split('/')[0] === innerActive;

    const renderSubItems = (subItems, parentRoute) => subItems.map((inner, j) => (
        <React.Fragment key={inner?.route ?? j}>
            <li
                className={`u_sbv2-sub-item ${isInnerActive(inner) ? 'u_sbv2-sub-item-active' : ''} ${inner?.isPartition ? 'u_menu-item-partition' : ''} ${inner?.commentFlag ? 'u_unread' : ''}`}
                onClick={(e) => handleInnerClick(e, inner, parentRoute)}
            >
                {inner?.tooltip
                    ? <UPopup params={{ className: 'u_tooltip' }} desc={<span>{inner.tooltip}</span>}><span>{inner?.name}</span></UPopup>
                    : inner?.name}
                {inner?.commentFlag && <span className="u_unread-sidemenu" />}
            </li>
            {inner?.devider && <li className="u_sbv2-divider" />}
        </React.Fragment>
    ));

    const nav = (
        <ul className="u_sbv2-menu">
            {items.map((item, i) => {
                const inner = accessibleInner(item);
                const isActive = active === item?.route || activeParentRoute === item?.route;
                const isExpanded = !collapsed && expandedKey === item?.route && !!inner.length;
                const railTag = item?.isBetaTag ? 'Beta' : (item?.isNewMenuTag ? 'New' : null);
                const isFlyoutOpen = collapsed && flyout?.route === item?.route;

                return (
                    <li
                        key={item?.route ?? i}
                        className={`u_sbv2-item ${isActive ? 'u_sbv2-item-active' : ''} ${isFlyoutOpen ? 'u_sbv2-item-flyout' : ''} ${item?.className || ''}`}
                    >
                        <div
                            className="u_sbv2-row"
                            onClick={(e) => handleItemClick(item, i, e)}
                        >
                            <span className="u_sbv2-icon">{item?.icon}</span>
                            <span className="u_sbv2-label">{item?.name}</span>

                            {!collapsed && (
                                <span className="u_sbv2-meta">
                                    {!!item?.messageBadge && <UBadge count={item.messageBadge} />}
                                    {!!item?.inboxBadge && <UCount count={item.inboxBadge} />}
                                    {!!item?.notificationBadge && <UBadge count={item.notificationBadge} />}
                                    {!!item?.isNewMenuTag && <UNewTag title="New" withoutPosition />}
                                    {!!item?.isBetaTag && <UNewTag title="Beta" withoutPosition />}
                                </span>
                            )}

                            {!collapsed && !!inner.length && (
                                <span className={`u_sbv2-chevron ${isExpanded ? 'u_sbv2-chevron-open' : ''}`}>
                                    <Icon.DownArrow size={12} fill={isActive ? COLOR.u_blue : COLOR.u_icon_bg} />
                                </span>
                            )}

                        </div>

                        {collapsed && !!railTag && (
                            <span className="u_sbv2-rail-tag">
                                <UNewTag title={railTag} withoutPosition />
                            </span>
                        )}

                        {isExpanded && (
                            <ul className="u_sbv2-sub">{renderSubItems(inner, item?.route)}</ul>
                        )}
                    </li>
                );
            })}
        </ul>
    );

    const flyoutNode = collapsed && flyout ? (
        <ul
            className="u_sbv2-flyout"
            style={{ top: `${flyout.top}px`, left: `${flyout.left}px` }}
        >
            <span className="u_sbv2-flyout-arrow" style={{ top: `${flyout.arrowTop}px` }} />
            {renderSubItems(flyout.items, flyout.route)}
        </ul>
    ) : null;

    return (
        <React.Fragment>
            {open && <div className="u_sbv2-overlay" onClick={onClose} />}

            {flyoutNode && typeof document !== 'undefined' && document.body
                ? ReactDOM.createPortal(flyoutNode, document.body)
                : flyoutNode}

            <aside
                className={`u_sbv2 ${collapsed ? 'u_sbv2-collapsed' : ''} ${open ? 'u_sbv2-open' : ''} ${className || ''}`}
                style={{ '--u-sbv2-width': `${width}px`, '--u-sbv2-collapsed-width': `${collapsedWidth}px` }}
            >
                {(header || collapsible) && (
                    <div className="u_sbv2-header">
                        {header && <div className="u_sbv2-header-slot">{header}</div>}

                        {collapsible && (
                            <button
                                type="button"
                                className={`u_sbv2-toggle ${collapsed ? 'u_sbv2-toggle-flip' : ''}`}
                                onClick={() => setCollapsed(!collapsed)}
                                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                                aria-expanded={!collapsed}
                            >
                                <Icon.SidebarCollapse size={17} stroke={COLOR.u_icon_bg} />
                            </button>
                        )}

                        {onClose && (
                            <button type="button" className="u_sbv2-close" onClick={onClose} aria-label="Close menu">
                                <Icon.Close size={12} fill={COLOR.u_blue} />
                            </button>
                        )}
                    </div>
                )}

                {beforeNav && <div className="u_sbv2-before-nav">{beforeNav}</div>}

                <div className="u_sbv2-body">
                    <UCustomScrollBar>{nav}</UCustomScrollBar>
                </div>

                {footer && <div className="u_sbv2-footer">{footer}</div>}
            </aside>
        </React.Fragment>
    );
};

USidebarV2.propTypes = {
    list: PropTypes.array,
    active: PropTypes.string,
    innerActive: PropTypes.string,
    onClick: PropTypes.func,
    onInnerClick: PropTypes.func,
    header: PropTypes.node,
    footer: PropTypes.node,
    beforeNav: PropTypes.node,
    open: PropTypes.bool,
    onClose: PropTypes.func,
    collapsed: PropTypes.bool,
    onCollapsedChange: PropTypes.func,
    collapsible: PropTypes.bool,
    storageKey: PropTypes.string,
    defaultCollapsed: PropTypes.bool,
    className: PropTypes.string,
    width: PropTypes.number,
    collapsedWidth: PropTypes.number,
};

USidebarV2.defaultProps = {
    list: [],
    active: '',
    innerActive: '',
    open: false,
    collapsible: true,
    storageKey: 'u_sidebar_collapsed',
    defaultCollapsed: false,
    width: 240,
    collapsedWidth: 90,
};

export default USidebarV2;
